import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { notificationAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import {
  Bell, BellOff, Check, CheckCheck, Trash2, Filter,
  FileText, Upload, AlertTriangle, CreditCard, MessageSquare,
  UserCheck, RefreshCw, ChevronRight, Inbox
} from 'lucide-react';
import { toast } from 'sonner';

const TYPE_META = {
  document_uploaded:  { icon: Upload,       color: 'bg-blue-100 text-blue-600',   label: 'Document' },
  document_approved:  { icon: Check,         color: 'bg-green-100 text-green-600', label: 'Document' },
  document_rejected:  { icon: AlertTriangle, color: 'bg-red-100 text-red-600',     label: 'Document' },
  case_updated:       { icon: FileText,      color: 'bg-teal-100 text-teal-600',   label: 'Case' },
  case_assigned:      { icon: UserCheck,     color: 'bg-teal-100 text-teal-600',   label: 'Case' },
  payment_received:   { icon: CreditCard,    color: 'bg-emerald-100 text-emerald-600', label: 'Payment' },
  payment_due:        { icon: CreditCard,    color: 'bg-amber-100 text-amber-600', label: 'Payment' },
  complaint_filed:    { icon: MessageSquare, color: 'bg-orange-100 text-orange-600', label: 'Complaint' },
  complaint_resolved: { icon: MessageSquare, color: 'bg-green-100 text-green-600', label: 'Complaint' },
  agreement_signed:   { icon: Check,         color: 'bg-indigo-100 text-indigo-600', label: 'Agreement' },
  system:             { icon: Bell,          color: 'bg-slate-100 text-slate-600', label: 'System' },
};

function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function NotificationsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | unread | read
  const [typeFilter, setTypeFilter] = useState('all');
  const [bulkMode, setBulkMode] = useState(false);
  const [selected, setSelected] = useState(new Set());

  const getBasePath = () => {
    switch (user?.role) {
      case 'super_admin': return '/admin';
      case 'process_manager': return '/manager';
      case 'case_officer': return '/officer';
      default: return '/client';
    }
  };

  const resolveLink = (notif) => {
    if (notif.link) return notif.link;
    if (notif.case_id) {
      const base = getBasePath();
      return `${base}/cases/${notif.case_id}`;
    }
    return null;
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await notificationAPI.getNotifications();
      setNotifications(res.data || []);
    } catch {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    try {
      await notificationAPI.markAsRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    } catch { /* silent */ }
  };

  const handleClick = async (notif) => {
    if (!notif.is_read) await markRead(notif.id);
    const link = resolveLink(notif);
    if (link) navigate(link);
  };

  const markAllRead = async () => {
    try {
      await notificationAPI.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      toast.success('All notifications marked as read');
    } catch { toast.error('Failed'); }
  };

  const deleteOne = async (id, e) => {
    e.stopPropagation();
    try {
      await notificationAPI.deleteNotification(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch { toast.error('Failed to delete'); }
  };

  const deleteSelected = async () => {
    try {
      await Promise.all([...selected].map(id => notificationAPI.deleteNotification(id)));
      setNotifications(prev => prev.filter(n => !selected.has(n.id)));
      setSelected(new Set());
      setBulkMode(false);
      toast.success(`${selected.size} notification(s) deleted`);
    } catch { toast.error('Failed to delete selected'); }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const filtered = notifications.filter(n => {
    if (filter === 'unread' && n.is_read) return false;
    if (filter === 'read' && !n.is_read) return false;
    if (typeFilter !== 'all' && n.type !== typeFilter) return false;
    return true;
  });

  const unreadCount = notifications.filter(n => !n.is_read).length;
  const uniqueTypes = [...new Set(notifications.map(n => n.type))];

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="main-content">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-200">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Bell size={24} className="text-teal-600" />
              Notifications
              {unreadCount > 0 && (
                <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  {unreadCount}
                </span>
              )}
            </h1>
            <p className="text-slate-500 text-sm mt-1">{filtered.length} notification{filtered.length !== 1 ? 's' : ''}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              className="p-2 text-slate-500 hover:bg-slate-100 rounded-lg transition-colors"
              title="Refresh"
            >
              <RefreshCw size={18} />
            </button>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
              >
                <CheckCheck size={16} /> Mark All Read
              </button>
            )}
            {bulkMode ? (
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">{selected.size} selected</span>
                {selected.size > 0 && (
                  <button
                    onClick={deleteSelected}
                    className="flex items-center gap-1 px-3 py-2 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600"
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                )}
                <button
                  onClick={() => { setBulkMode(false); setSelected(new Set()); }}
                  className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setBulkMode(true)}
                className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50"
              >
                Select
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
            {['all', 'unread', 'read'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium capitalize transition-all ${
                  filter === f ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {f === 'all' ? `All (${notifications.length})` : f === 'unread' ? `Unread (${unreadCount})` : `Read (${notifications.length - unreadCount})`}
              </button>
            ))}
          </div>

          {uniqueTypes.length > 1 && (
            <div className="flex items-center gap-2">
              <Filter size={16} className="text-slate-400" />
              <select
                value={typeFilter}
                onChange={e => setTypeFilter(e.target.value)}
                className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-white outline-none"
              >
                <option value="all">All Types</option>
                {uniqueTypes.map(t => (
                  <option key={t} value={t}>{TYPE_META[t]?.label || t}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Notification List */}
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-slate-200 border-t-teal-600"></div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-slate-400">
            <Inbox size={56} className="mb-4 text-slate-300" />
            <p className="text-lg font-medium text-slate-500">No notifications</p>
            <p className="text-sm mt-1">
              {filter !== 'all' ? 'Try switching to "All"' : "You're all caught up!"}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100 overflow-hidden">
            {filtered.map(notif => {
              const meta = TYPE_META[notif.type] || TYPE_META.system;
              const Icon = meta.icon;
              const link = resolveLink(notif);

              return (
                <div
                  key={notif.id}
                  onClick={() => !bulkMode && handleClick(notif)}
                  className={`flex items-start gap-4 px-5 py-4 transition-all ${
                    !notif.is_read ? 'bg-teal-50/40' : ''
                  } ${!bulkMode && link ? 'cursor-pointer hover:bg-slate-50' : ''}`}
                >
                  {bulkMode && (
                    <input
                      type="checkbox"
                      checked={selected.has(notif.id)}
                      onChange={() => toggleSelect(notif.id)}
                      onClick={e => e.stopPropagation()}
                      className="mt-1 w-4 h-4 rounded border-slate-300 text-teal-600 cursor-pointer flex-shrink-0"
                    />
                  )}

                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${meta.color}`}>
                    <Icon size={18} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className={`text-sm font-semibold ${!notif.is_read ? 'text-slate-900' : 'text-slate-700'}`}>
                          {notif.title}
                          {!notif.is_read && (
                            <span className="ml-2 inline-block w-2 h-2 bg-teal-500 rounded-full align-middle"></span>
                          )}
                        </p>
                        <p className="text-sm text-slate-500 mt-0.5 leading-relaxed">{notif.message}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-xs text-slate-400 whitespace-nowrap">{timeAgo(notif.created_at)}</span>
                        {!notif.is_read && !bulkMode && (
                          <button
                            onClick={e => { e.stopPropagation(); markRead(notif.id); }}
                            className="p-1 text-slate-400 hover:text-teal-600 hover:bg-teal-50 rounded transition-colors"
                            title="Mark as read"
                          >
                            <Check size={14} />
                          </button>
                        )}
                        {!bulkMode && (
                          <button
                            onClick={e => deleteOne(notif.id, e)}
                            className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                    {link && !bulkMode && (
                      <div className="mt-1.5 flex items-center gap-1 text-xs text-teal-600 font-medium">
                        View details <ChevronRight size={12} />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
